import React, { useCallback, useState } from 'react'

import { HiChevronRight } from 'react-icons/hi2'

import { cn } from '@/helpers/shadcn/utils'

export interface IntroWizard {
  step: number
  isLast: boolean
  /** Entry animation for the active step's content, direction-aware. Key the
   * animated wrapper by `step` so it re-mounts (and re-animates) per step. */
  enterClass: string
  handleNext: () => void
  handleBack: () => void
}

/**
 * Step state for an announcement intro wizard: clamped forward/back
 * navigation plus the direction-aware enter animation class.
 */
export const useIntroWizard = (stepCount: number): IntroWizard => {
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')

  const handleNext = useCallback(() => {
    setDirection('forward')
    setStep((current) => Math.min(stepCount - 1, current + 1))
  }, [stepCount])

  const handleBack = useCallback(() => {
    setDirection('back')
    setStep((current) => Math.max(0, current - 1))
  }, [])

  return {
    step,
    isLast: step === stepCount - 1,
    enterClass:
      direction === 'back'
        ? 'animate-in fade-in slide-in-from-left-4 duration-300'
        : 'animate-in fade-in slide-in-from-right-4 duration-300',
    handleNext,
    handleBack,
  }
}

interface StepDotsProps {
  count: number
  step: number
}

export const StepDots: React.FC<StepDotsProps> = ({ count, step }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: count }, (_, index) => (
      <span
        key={index}
        className={cn(
          'h-1.5 rounded-full transition-all duration-300',
          index === step
            ? 'w-5 bg-accent-500'
            : 'w-1.5 bg-primary-300 dark:bg-primary-600',
        )}
      />
    ))}
  </div>
)

interface WizardContinueButtonProps {
  onClick: () => void
}

export const WizardContinueButton: React.FC<WizardContinueButtonProps> = ({
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-10 items-center gap-1.5 rounded-[10px] bg-gradient-to-br from-accent-400 to-accent-500 px-5 text-sm font-semibold text-white shadow-[0_6px_16px_-8px_rgba(236,72,153,0.55)] transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 active:scale-[0.97] dark:focus-visible:ring-offset-default-dark"
  >
    Continue
    <HiChevronRight className="h-4 w-4" />
  </button>
)

interface ShimmerCtaButtonProps {
  onClick: () => void
  /** Sizing/border/shadow tweaks on top of the shared violet-gradient base. */
  className?: string
  children: React.ReactNode
}

/** The violet-gradient AI call-to-action with a hover shimmer sweep. */
export const ShimmerCtaButton: React.FC<ShimmerCtaButtonProps> = ({
  onClick,
  className,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'group relative inline-flex items-center justify-center overflow-hidden whitespace-nowrap rounded-[10px] bg-gradient-to-br from-violet-500 to-indigo-600 text-sm font-semibold text-white transition-transform duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 active:scale-[0.97] dark:from-violet-600 dark:to-indigo-700 dark:focus-visible:ring-offset-default-dark',
      className,
    )}
  >
    <span
      aria-hidden={true}
      className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-0 ease-out group-hover:translate-x-full group-hover:duration-1000"
    />
    <span className="relative inline-flex items-center gap-2">{children}</span>
  </button>
)
